<template>
  <div>
    <b-modal id="variable-calculation-modal" ref="variableCalculationModal" :hide-header-close="true" hide-footer :no-close-on-esc="noCloseOnBackdrop" :no-close-on-backdrop="noCloseOnBackdrop">
      <template slot="modal-title">Create Variable Calculation</template>
      <i class="fa fa-times col-3 text-right close-modal-btn" aria-hidden="true" @click="hide('close-modal')"></i>
      <b-tabs v-model="tabIndex" content-class="mt-0">
        <b-tab title="Formula" id="formula-tab">
          <div class="formula-input-container">
            <div class="edit-variable-name">
              <div v-if="isEditingVariableName" class="save-variable-wrap">
                <input class="form-control input-variable-name" :class="error.variableError ? 'input-error' : ''" v-model.trim="variableName" ref="inputVariableName" type="text" autofocus="autofocus" />
                <b-button data-testid="save-variable" variant="primary" :disabled="disableVariableSaveBtn" @click="updateVariable()"><i class="fa fa-check fs-24" aria-hidden="true"></i></b-button>
                <b-button data-testid="cancel-variable" class="btn-cancel" @click="cancelVariable()"><img src="static/img/cross.png"/></b-button>
              </div>
              <div v-else>
                <div v-if="variableCalculation.name == ''"><input class="form-control input-variable-name default-variable-name" :class="error.variableError ? 'input-error' : ''" v-model.trim="variableName" ref="inputVariableName" type="text" autofocus="autofocus" placeholder="Enter Variable Name" @click="editVariable" /></div>
                <div v-else class="variable-name">
                  <div class="show-var-name">{{ variableCalculation.name }}</div>
                  <span data-testid="edit-variable" class="edit-icon ml-8 mr-8" @click="editVariable"><i class="fa fa-pencil edit_hover_class fs-18" style="color: #4c8ce4;" aria-hidden="true"></i></span>
                  =
                </div>
              </div>
              <span class="validation-error" v-if="error.variableError">{{ error.variableError }}</span>
            </div>
            <b-form-textarea :disabled="isEditingVariableName" :class="error.formulaError ? 'formula-error' : ''" v-model="variableCalculation.formula" placeholder="Type your formula here or use the calculation buttons below." rows="4" max-rows="6" id="formulaTextArea" v-on:click="getCursorValue()"></b-form-textarea>
            <span class="validation-error" v-if="error.formulaError">{{ error.formulaError }}</span>
          </div>
          <div class="buttons-container" :class="{ 'disable-button-container': isEditingVariableName }">
            <b-row :no-gutters="true">
              <b-col class="left-buttons pr-26" cols="5">
                <CustomSelectDropdown data-testid="add-step-dropdown" buttonText="Add Step(s)" addButtonText="Steps" addButtonSingleText="Step" :dropdownContent="getStepData()" @onDropdownSelect="onStepSelect" @onAddButtonClick="onAddBtnClick('step')"></CustomSelectDropdown>
                <CustomSelectDropdown buttonText="Add Section(s)" addButtonText="Sections" addButtonSingleText="Section" :dropdownContent="getSectionData()" @onDropdownSelect="onSectionSelect" @onAddButtonClick="onAddBtnClick('section')"></CustomSelectDropdown>
                <CustomSelectDropdown buttonText="Add Group Tag(s)" addButtonText="Group Tags" addButtonSingleText="Group Tag" :dropdownContent="groupTags" @onDropdownSelect="onGroupTagSelect" :isElementDisabled="isGroupTagDisabled" @onAddButtonClick="onAddBtnClick('group-tags')"></CustomSelectDropdown>
                <b-button block variant="outline-primary" disabled>Add Custom Variable</b-button>
              </b-col>
              <b-col class="right-buttons" cols="7">
                <div class="btn-wrapper">
                  <b-button variant="outline-primary" data-testid="sum" class="airthmetic-btn" @click="appendFormulaString('SUM()')" @keydown.space.prevent @keydown.enter.prevent>SUM ( )</b-button>
                  <b-button variant="outline-primary" class="airthmetic-btn" @click="appendFormulaString('AVG()')" @keydown.space.prevent @keydown.enter.prevent>AVG ( )</b-button>
                  <b-button variant="outline-primary" class="airthmetic-btn" @click="appendFormulaString('MAX()')" @keydown.space.prevent @keydown.enter.prevent>MAX ( )</b-button>
                  <b-button variant="outline-primary" class="airthmetic-btn" @click="appendFormulaString('MIN()')" @keydown.space.prevent @keydown.enter.prevent>MIN ( )</b-button>
                  <b-button variant="outline-primary" @click="appendFormulaString('COUNT()')" @keydown.space.prevent @keydown.enter.prevent>COUNT ( )</b-button>
                  <b-button variant="outline-primary" @click="appendFormulaString('COUNTIF()')" @keydown.space.prevent @keydown.enter.prevent>COUNTIF ( )</b-button>
                  <b-button variant="outline-primary" @click="appendFormulaString('COUNTANSWERED()')" @keydown.space.prevent @keydown.enter.prevent>COUNTANSWERED ( )</b-button>
                  <b-button @click="appendFormulaString('SQRT()')" @keydown.space.prevent @keydown.enter.prevent id="btn-sqrt"><img src="static/img/calculator-button.svg" alt="sqrt" /></b-button>
                  <b-button @click="appendFormulaString('^')" @keydown.space.prevent @keydown.enter.prevent id="btn-exponents"><img src="static/img/calc-btn.svg" alt="exponents" /></b-button>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-tab>
        <b-tab title="Settings" id="settings-tab">
          <div class="title">Variable Visibility</div>
          <div class="visibility-options">
            <div class="global-visibility">
              <input v-model="variableVisibility" type="radio" value="global" id="globalOption" data-testid="global-radio-btn" />
              <label class="visibility-radio-label" for="globalOption">Global</label>
              <div class="option-text">Variable will be available everywhere: data exports, summary screens, etc.</div>
              <div class="ml-32 mt-12 checkbox-wrapper" :class="{ disabled: variableVisibility == 'local' }">
                <input id="displayVarInPortal" v-model="displayVarInPortal" name="displayVarInPortal" type="checkbox" data-testid="display-var-in-portal" />
                <label for="displayVarInPortal">Display variable in portal</label>
              </div>
            </div>
            <div class="local-visibility">
              <input v-model="variableVisibility" type="radio" value="local" id="localOption" data-testid="local-radio-btn" />
              <label class="visibility-radio-label" for="localOption">Local</label>
              <div class="option-text">Variable will be used exclusively for reference within the scoring module and <br />is hidden from all other screens.</div>
            </div>
          </div>
          <div class="decimal-places mt-56">
            <div class="title">Decimal Places</div>
            <div class="decimal-dropdown mt-18 mb-12">
              <select v-model="variableCalculation.decimals">
                <option v-for="(decimalPlace, index) in decimalPlacesOptions" :value="`${index}`" :key="`decimal-place-${index}`">{{ decimalPlace }}</option>
              </select>
            </div>
            <div class="round-number checkbox-wrapper">
              <input id="roundNumber" v-model="variableCalculation.rounding" name="rounding" type="checkbox" />
              <label for="roundNumber">Round number</label>
            </div>
          </div>
        </b-tab>
        <div>
          <b-button class="reassign-btn border-0" :class="this.showReassignValue ? 'disable-reassign-btn' : ''" v-if="showReassignValueCount">
            <span @click="openReassignValueModal(showReassignValueCount)">View {{ showReassignValueCount }} Reassigned Values</span>
          </b-button>
          <b-button class="reassign-btn" :class="this.showReassignValue ? 'disable-reassign-btn' : ''" v-else>
            <span v-if="this.showReassignValue" v-b-popover.hover.focus="'Unsaved variables cannot have reassigned values. Please save your variable and try again.'">Reassign Values to New Variable</span>
            <span v-else @click="openReassignValueModal()">Reassign Values to New Variable</span>
          </b-button>
        </div>
      </b-tabs>
      <template #modal-footer>
        <div class="footer" :class="{ 'disable-modal-footer': isEditingVariableName }">
          <div class="my-auto text-right">
            <b-button class="btn-cancel" @click="hide()">
              Cancel
            </b-button>
            <b-button :disabled="disableSaveButton" variant="primary" @click="saveVariable()">
              Save Variable
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
    <reassignValueModal ref="reassignValueModal" :newVariableList="this.variableCalculation" :variablesListData="this.variablesListData" />
  </div>
</template>

<script>
import CustomSelectDropdown from "@/components/activity-library/custom-select-dropdown";
import studyActivityCommon from "@/components/configurator/study-activities-mixin";
import reassignValueModal from "@/components/activity-library/reassign-value-modal";
import { cloneDeep } from "lodash";
export default {
  name: "variable-calculation-modal-component",
  data() {
    return {
      noCloseOnBackdrop: true,
      tabIndex: 0,
      activityId: this.$route.params.activityId,
      variableCalculation: {
        name: "",
        formula: "",
        decimals: 0,
        rounding: false,
        facing: "",
        globalVisibility: "global_site",
        reassignedMapping: {},
        reassignedValueVariable: ""
      },
      displayVarInPortal: true,
      variableVisibility: "global",
      activitySteps: [],
      selectedSteps: "",
      selectedSections: "",
      selectedGroupTags: "",
      groupTags: [],
      isEditingVariableName: false,
      variableName: "",
      decimalPlacesOptions: ["0", "0.0", "0.00", "0.000", "0.0000", "0.00000", "0.000000"],
      activityData: {},
      error: {},
      formulaCursorPosition: undefined,
      showReassignValue: false,
      showReassignValueCount: "",
      questionIdToStepSequenceMap: {},
      isGroupTagDisabled: element => !(element && element.steps && element.steps.some(s => s))
    };
  },
  props: {
    variablesListData: {
      type: Array
    }
  },
  components: { CustomSelectDropdown, reassignValueModal },
  mixins: [studyActivityCommon],
  computed: {
    disableSaveButton() {
      //This computed property will handle enabling/disabling of save button
      return false;
    },
    disableVariableSaveBtn() {
      delete this.error.variableError;

      if (!this.variableName || this.variableName == "") {
        return true;
      }

      if (!/^[a-zA-Z_][a-zA-Z0-9_ ]*$/.test(this.variableName)) {
        this.error.variableError = 'Variable name cannot start with a number or contain non-alphanumeric characters.';
        return true;
      }

      return false;
    }
  },
  watch: {
    variableVisibility(visibilityValue) {
      // check if we are setting the variable to local or global
      // if local, uncheck the checkbox in "global" by default
      // if global, check the checkbox in "global" automatically
      if (visibilityValue === "local") {
        this.displayVarInPortal = false;
      } else {
        this.displayVarInPortal = true;
      }
    },
    "variableCalculation.formula": {
      deep: true,
      handler() {
        this.formulaCursorPosition = $("#formulaTextArea").prop("selectionStart");
      }
    }
  },
  methods: {
    show(variableObject = null) {
      this.showReassignValue = true;
      this.getActivityFacingType();
      this.variableCalculation = {
        name: "",
        formula: "",
        decimals: 0,
        rounding: false,
        facing: "",
        globalVisibility: "global_site",
        reassignedMapping: {},
        reassignedValueVariable: ""
      };
      this.$refs.variableCalculationModal.show();
      if (variableObject) {
        this.showReassignValue = false;
        this.variableCalculation = cloneDeep(variableObject);
        this.variableCalculation.reassignedValueVariable = variableObject && variableObject.reassignedValueVariable;
        this.variableCalculation.reassignedMapping = variableObject && variableObject.reassignedMapping;
        this.showReassignValueCount = variableObject && variableObject.reassignedMapping && variableObject.reassignedMapping.length;
        this.setVariableVisibility();
      }
    },
    hide() {
      this.$refs.variableCalculationModal.hide();
      this.resetValues();
      this.showReassignValueCount = "";
    },
    openReassignValueModal(showReassignValueCount) {
      if (this.showReassignValueCount) {
        this.$refs.reassignValueModal.show(showReassignValueCount, this.variableCalculation);
        this.showReassignValueCount = "";
      } else {
        this.$refs.reassignValueModal.show();
      }
    },
    resetValues() {
      this.variableCalculation = {
        name: "",
        formula: "",
        decimals: 0,
        rounding: false,
        facing: "",
        globalVisibility: "global_site"
      };
      this.displayVarInPortal = true;
      this.variableVisibility = "global";
      this.tabIndex = 0;
      this.resetErrors();
      this.cancelVariable();
    },
    editVariable() {
      this.variableName = this.variableCalculation.name;
      this.isEditingVariableName = true;
      this.$nextTick(() => this.$refs.inputVariableName.focus());
    },
    emptyVariableName() {
      this.variableName = "";
    },
    updateVariable() {
      this.variableCalculation.name = this.variableName;
      this.emptyVariableName();
      this.isEditingVariableName = false;
      this.resetErrors();
    },
    cancelVariable() {
      this.isEditingVariableName = false;
      this.emptyVariableName();
      this.$set(this.error, "variableError", "");
    },
    appendFormulaString(formulaStr) {
      this.getCursorValue();
      let index = this.formulaCursorPosition;
      if (index >= 0) {
        this.variableCalculation.formula = [this.variableCalculation.formula.slice(0, index), formulaStr, this.variableCalculation.formula.slice(index)].join("");
      } else {
        this.variableCalculation.formula = this.variableCalculation.formula + formulaStr;
      }
    },
    onStepSelect(selectedStepsList) {
      this.selectedSteps = "";
      selectedStepsList.map((step, index) => {
        if (index === selectedStepsList.length - 1) {
          this.selectedSteps = this.selectedSteps + ("Step " + (step.stepSequence ? step.stepSequence : step.sequence));
        } else {
          this.selectedSteps = this.selectedSteps + ("Step " + (step.stepSequence ? step.stepSequence : step.sequence) + ", ");
        }
      });
    },
    onSectionSelect(selectedSections) {
      this.selectedSections = "";
      selectedSections.map((section, index) => {
        if (index === selectedSections.length - 1) {
          this.selectedSections = this.selectedSections + section.title;
        } else {
          this.selectedSections = this.selectedSections + (section.title + ", ");
        }
      });
    },
    //To add title and subtitle for dropdown
    addTitleAndSubTitle() {
      this.activitySteps.map(data => {
        if (data.type !== "SurveyCompletion" && data.type !== "Section") {
          data.title = "Step " + (data.stepSequence ? data.stepSequence : data.sequence) + " - " + this.getQuestionTitleByType(data.type);
          data.subTitle = data.body;
          this.questionIdToStepSequenceMap[data.id] = `Step ${data.stepSequence ? data.stepSequence : data.sequence}`;
        } else if (data.type === "Section") {
          let sections = this.getSectionsWithSteps();
          Object.keys(sections).forEach(sectionId => {
            if (sectionId === data.id) {
              let questions = sections[sectionId];
              questions = questions.filter(ques => {
                return ques.type !== "SurveyCompletion";
              });
              if (questions.length === 1) {
                data.subTitle = "Step " + (questions[0].stepSequence ? questions[0].stepSequence : questions[0].sequence);
              } else {
                data.subTitle = "Step " + (questions[0].stepSequence ? questions[0].stepSequence : questions[0].sequence) + " - " + "Step " + (questions[questions.length - 1].stepSequence ? questions[questions.length - 1].stepSequence : questions[questions.length - 1].sequence);
              }
            }
          });
        }
      });
    },
    getQuestionTitleByType(type) {
      if (type == "SurveyInstruction") {
        return "Instruction screen";
      }
      if (type == "CustomScale") {
        return "Custom scale";
      }
      if (type == "DateTime") {
        return "Date / Time";
      }
      if (type == "DropDown") {
        return "Drop-down";
      }
      if (type == "GPSkin") {
        return "gpskin Barrier device scan";
      }
      if (type == "ImageCapture") {
        return "Image capture";
      }
      if (type == "Image") {
        return "Image choice";
      }
      if (type == "Index") {
        return "Index";
      }
      if (type == "Log") {
        return "Log Entry";
      }
      if (type == "Measurements") {
        return "Measurements";
      }
      if (type == "MedicationSelector") {
        return "Medication Selector";
      }
      if (type == "MultiChoice") {
        return "Multiple choice";
      }
      if (type == "Number") {
        return "Number Response";
      }
      if (type == "NumberScale") {
        return "Number Scale";
      }
      if (type == "Text") {
        return "Text Response";
      }
      if (type == "ValidatedEntry") {
        return "Validated Entry";
      }
      if (type == "VAS") {
        return "Visual analog scale";
      }
      if (type == "Boolean") {
        return "Yes or No";
      }
      if (type == "WongBaker") {
        return "Wong-Baker FACES Pain Rating Scale";
      }
      if (type == "AudioRecording") {
        return "Audio Recording";
      }
      if (type == "VideoRecording") {
        return "Video Recording";
      }
      if (type == "SkinAnalyser") {
        return "Redness and Dryness Assessment";
      }
    },
    getSectionsWithSteps() {
      let sections = {};
      let questions = [];
      let section;
      this.activitySteps.forEach(question => {
        if (question.type == "Section") {
          if (questions.length > 0) {
            if (section) {
              sections[section.id] = questions;
            }
          }
          section = question;
          questions = [];
        } else {
          questions.push(question);
        }
      });
      if (section) {
        sections[section.id] = questions;
      }
      return sections;
    },
    getStepData() {
      if (this.activitySteps && this.activitySteps.length > 0) {
        return this.activitySteps.filter(step => {
          return step.type !== "SurveyCompletion" && step.type !== "Section" && step.type !== "SurveyInstruction";
        });
      }
    },
    getSectionData() {
      if (this.activitySteps && this.activitySteps.length > 0) {
        return this.activitySteps.filter(step => {
          return step.type === "Section";
        });
      }
    },
    async getActivityDetails() {
      this.activityData = this.$store.state.advancedScoringSteps;
      if (this.activityData && this.activityData.questions > 0) {
        this.activitySteps = this.activityData.questions;
        this.getActivityFacingType();
        this.addTitleAndSubTitle();
      } else {
        await this.$api.getActivityDetails(this.activityId).then(response => {
          this.activitySteps = response.body.result.questions;
          this.activityData = response.body.result;
          this.getActivityFacingType();
          this.addTitleAndSubTitle();
        });
      }
    },
    addDropdownDataToFormula(data) {
      this.getCursorValue();
      let index = this.formulaCursorPosition;
      if (index >= 0) {
        this.variableCalculation.formula = [this.variableCalculation.formula.slice(0, index), data, this.variableCalculation.formula.slice(index)].join("");
      } else {
        this.variableCalculation.formula = this.variableCalculation.formula + data;
      }
    },
    onAddBtnClick(type) {
      if (type === "step") {
        this.addDropdownDataToFormula(this.selectedSteps);
      } else if (type === "section") {
        this.addDropdownDataToFormula(this.selectedSections);
      } else if (type === "group-tags") {
        this.addDropdownDataToFormula(this.selectedGroupTags);
      }
    },
    onGroupTagSelect(groupTags) {
      this.selectedGroupTags = groupTags.map(tag => tag.name).join(", ");
    },
    saveVariable() {
      this.resetErrors();
      if (this.variableVisibility == "local") {
        this.variableCalculation.globalVisibility = "local";
      } else if (this.variableVisibility == "global" && this.displayVarInPortal) {
        this.variableCalculation.globalVisibility = "global_site";
      } else {
        this.variableCalculation.globalVisibility = "global";
      }
      let activityId = this.$route.query.routeActivityId;
      let pathParams = {
        activityId: activityId,
        variableId: this.variableCalculation.id,
        activityRevisionId: this.activityData.id
      };

      let resourceMethod = this.variableCalculation.id ? "updateVariable" : "createVariable";
      this.$api[resourceMethod](pathParams, this.variableCalculation)
        .then(() => {
          this.$toastr.success("Variable saved successfully");
          this.hide();
          this.$emit("refreshVariablesList");
        })
        .catch(error => {
          let errorKey = error.body.key;
          if (errorKey === "DUPLICATE_VARIABLE_NAME") {
            this.$set(this.error, "variableError", "Variable name already used. Please choose a different name.");
            this.editVariable();
          } else if (errorKey === "INVALID_VARIABLE_FORMULA") {
            this.$set(this.error, "formulaError", "Validation error. Please make sure the calculation entered is complete.");
          } else if (errorKey === "EMPTY_VARIABLE_FORMULA") {
            this.$set(this.error, "formulaError", "Please type your formula here or use the calculation buttons below.");
          } else if (errorKey === "EMPTY_VARIABLE_NAME") {
            this.$set(this.error, "variableError", "Variable Name cannot be empty.");
          } else {
            this.$toastr.error(error.body ? error.body.message : "Error while saving variable.");
          }
          //Switch back to formula tab
          this.tabIndex = 0;
        });
    },
    async getActivityFacingType() {
      let isSiteFacing = await this.determineActivity(this.activityData);
      if (isSiteFacing) {
        this.variableCalculation.facing = "site";
      } else {
        this.variableCalculation.facing = "participant";
      }
    },
    setVariableVisibility() {
      //Execute below code only for edit/update case
      if (this.variableCalculation.globalVisibility == "local") {
        this.variableVisibility = "local";
        this.displayVarInPortal = false;
      } else if (this.variableCalculation.globalVisibility == "global_site") {
        this.variableVisibility = "global";
        this.displayVarInPortal = true;
      } else {
        this.variableVisibility = "global";
        this.displayVarInPortal = false;
      }
    },
    resetErrors() {
      this.error = {};
    },
    getCursorValue() {
      this.formulaCursorPosition = $("#formulaTextArea").prop("selectionStart");
    },
    getGroupTagData() {
      this.$api.getAllGroupTags({ activityRevisionId: this.$route.params.activityId }).then(
        response => {
          if (response.body && response.body.result && response.body.result.groupTags) {
            const groupTags = response.body.result.groupTags;
            this.groupTags = groupTags
              .map(tag => ({
                ...tag,
                title: tag.name,
                subTitle: tag.steps
                  .map(stepId => this.questionIdToStepSequenceMap[stepId])
                  .filter(Boolean)
                  .join(", ")
              }))
              .sort(({ name: a }, { name: b }) => (a.toLowerCase() < b.toLowerCase() ? -1 : 1));
          }
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting the tags details.");
        }
      );
    }
  },
  async mounted() {
    await this.getActivityDetails();
    this.getGroupTagData();
    this.$root.$on("showVariableCalculationModal", data => {
      this.variableCalculation.reassignedValueVariable = data.reassignedVariableName;
      if (data.reassignData) {
        this.variableCalculation.reassignedMapping = data.reassignData;
        this.showReassignValueCount = data.reassignData.length;
        this.show(data.variableData);
      }
      if (data) {
        if (data.variableData) {
          this.show(data.variableData);
        }
      }
    });
  }
};
</script>

<style lang="scss">
#variable-calculation-modal {
  @media (min-width: 992px) {
    .modal-dialog {
      min-width: 599px;
      vertical-align: middle;
    }
  }
  .modal-content {
    .modal-header {
      border-bottom: 0;
      color: #4a4a4a;
      padding: 24px 32px;
      padding-bottom: 0;
      font-family: Roboto-Medium;

      h5 {
        font-size: 1.375rem;
      }

      .close {
        opacity: 1;
        font-size: 30px;
        color: #666669;
        padding: 12px;
      }
    }

    .modal-body {
      padding: 0;
      padding-top: 5px;
      font-size: 14px;
      color: #4a4a4a;

      .close-modal-btn {
        cursor: pointer;
        position: absolute;
        right: 8px;
        top: -34px;
        color: #666669;
        font-size: 22px;
      }

      .btn {
        &:active {
          background: none;
          border: none;
        }

        &:focus {
          box-shadow: none;
        }
      }

      .tabs {
        margin-top: 32px;

        .nav {
          &.nav-tabs {
            margin-left: 2rem;
            margin-right: 2rem;

            .nav-item {
              margin-right: 33px;

              .nav-link {
                border: transparent;
                padding: 0;
                font-family: Roboto-Medium;
                font-size: 14px;
                color: #666669;
                opacity: 0.6;

                &.active {
                  border-bottom: 4px solid #4c8ce4;
                  opacity: 1;
                }
              }
            }
          }
        }

        #settings-tab {
          padding: 2rem;

          .title {
            font-family: Roboto-Medium;
            font-size: 16px;
            color: #4a4a4a;
          }

          /*checkbox data*/
          .checkbox-wrapper {
            display: inline-block;
            cursor: pointer;
            font-size: 14px;
            color: #666669;
            margin-bottom: 0;

            input {
              margin-right: 10px;
              accent-color: #1e8fe1;
            }

            label {
              cursor: pointer;
              margin-bottom: 0;
            }

            &.disabled {
              pointer-events: none;
              opacity: 0.4;
            }

            input[type="checkbox"] {
              height: 20px;
              width: 20px;
              vertical-align: middle;
            }
          }

          .visibility-options {
            margin-top: 16px;
            font-family: Roboto-Regular;
            font-size: 12px;
            color: #7c7c7c;

            .visibility-radio-label {
              font-family: Roboto-Bold;
              font-size: 16px;
              color: #666669;
              margin-left: 16px;
              cursor: pointer;
              vertical-align: middle;
              margin-bottom: 0;
            }

            .option-text {
              margin-left: 32px;
            }

            .global-visibility {
              margin-bottom: 24px;
            }

            input[type="radio"] {
              height: 16px;
              width: 16px;
              vertical-align: middle;
              cursor: pointer;
              accent-color: #1e8fe1;
            }
          }

          .decimal-places {
            .decimal-dropdown {
              select {
                border-radius: 4px;
                border: solid 1px #d3dfe4;
                width: 173px;
              }
            }
          }
        }
      }

      .formula-input-container {
        font-size: 14px;
        padding: 2rem;

        .variable-name {
          color: #666669;
          margin-bottom: 12px;
          display: flex;
          align-items: flex-start;

          .show-var-name {
            word-break: break-all;
          }
        }

        .edit-variable-name {
          margin-bottom: 16px;

          .save-variable-wrap {
            display: flex;
            vertical-align: middle;
            align-items: normal;

            button {
              font-size: 14px;

              &.btn-primary {
                background-color: rgba(72, 191, 122, 0.3);
                border-color: rgba(72, 191, 122, 0.3);
                border-radius: 4px;
                padding: 0;
                margin-left: 12px;
                width: 38px;
                height: 38px;

                &:disabled {
                  opacity: 0.4;
                  cursor: no-drop;
                }

                &:focus {
                  box-shadow: none;
                }

                i {
                  color: #17974d;
                  position: relative;
                  top: 1px;
                }
              }

              &.btn-cancel {
                background: rgba(224, 224, 224, 0.5);
                margin-left: 8px;
                border: 0;
                padding: 0;
                width: 38px;
                height: 38px;

                img {
                  height: 40px;
                  width: 40px;
                }

                &:active {
                  background: none;
                  border: none;
                }

                &:focus {
                  box-shadow: none;
                }
              }
            }
          }
        }

        .edit-icon {
          cursor: pointer;
        }

        .input-variable-name {
          border: solid 1px #4c8ce4;
          background-color: #f5f8fa;
          border-radius: 4px;
          color: #666669;
          padding: 2px;
          height: 36px;
          padding-left: 12px;
          width: 83%;
        }

        .default-variable-name {
          width: 100%;
        }

        textarea {
          border-radius: 4px;
          border: solid 2px #4c8ce4;
          background-color: #f5f8fa;
          font-family: "Roboto-Medium";
          color: #4a4a4a;

          &:disabled {
            opacity: 0.4;
            pointer-events: none;
          }
        }

        .validation-error {
          font-size: 13px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.33;
          letter-spacing: normal;
          text-align: left;
          color: #f16559;
        }

        .input-error {
          border: solid 1px #f16559;
          box-shadow: none;
        }

        .formula-error {
          border: solid 2px #f16559;
          margin-bottom: 9px;
          box-shadow: none;
        }
      }

      .buttons-container {
        padding: 2rem;
        padding-bottom: 0;
        background-color: #f5f8fa;

        &.disable-button-container {
          opacity: 0.6;
          pointer-events: none;
        }

        .btn-outline-primary {
          background-color: #fff;
          border: solid 1px #b0bac9;
          font-size: 12px;
          font-family: Roboto-Bold;
          color: #4c8ce4;

          &:hover {
            background-color: #fff;
          }

          &:disabled {
            opacity: 0.4;
            pointer-events: none;
          }
        }

        .left-buttons {
          flex: 0 0 36%;
          max-width: 36%;
        }

        .right-buttons {
          flex: 0 0 64%;
          max-width: 64%;

          .btn {
            margin-bottom: 8px;
            margin-right: 8px;
            padding: 6px 14px;

            &.airthmetic-btn {
              padding: 6px 19px;
            }

            &#btn-sqrt {
              padding: 0;
              border: 0;
              img {
                height: auto;
                width: 70px;
              }
            }

            &#btn-exponents {
              padding: 0;
              border: 0;
              img {
                height: auto;
                width: 50px;
              }
            }

            &:nth-child(4) {
              margin-right: 0;
              padding: 6px 20px;
            }

            &:last-of-type {
              margin-right: 0;
            }
          }

        }

        .disable-dropdown {
          opacity: 0.4;
          pointer-events: none;
        }
      }

      .footer {
        padding: 2rem;
        background-color: #f5f8fa;

        &.disable-modal-footer {
          opacity: 0.6;
          pointer-events: none;
        }

        button {
          font-size: 14px;

          &.btn-primary {
            background-color: #4c8ce4;
            border-color: #4c8ce4;
            border-radius: 4px;
            padding: 7px 16px;
            font-family: Roboto-Bold;

            &:hover {
              background-color: #34537d;
              border-color: #34537d;
            }

            &:disabled {
              opacity: 0.4;
              pointer-events: none;
            }

            &:focus {
              box-shadow: none;
            }
          }

          &.btn-cancel {
            padding: 8px 24px;
            color: #666669;
            background: none;
            border: 0;
            font-family: Roboto-Medium;

            &:hover {
              color: #4a4a4a;
              background: none;
              border: none;
              padding: 8px 24px;
              outline: none;
              box-shadow: none;
            }

            &:active {
              background: none;
              border: none;
            }

            &:focus {
              box-shadow: none;
            }

            &:disabled {
              opacity: 0.4;
              pointer-events: none;
            }
          }
        }
      }

      .reassign-btn {
        position: absolute;
        top: 23px;
        right: 2rem;
        border: 1px solid #4c8ce4;
        background: #fff;
        color: #4c8ce4;
        font-size: 14px;
        font-family: Roboto-Bold;
        padding: 8px 16px;
      }

      .disable-reassign-btn {
        opacity: 0.4;
      }
    }
  }
}
.popover {
  transform: translate3d(288px, 124px, 0px) !important;
}
.popover-body {
  background: #000 !important;
  color: #fff;
  width: 288px;
  text-align: center;
  font-size: 12px;
  border-radius: 4px;
}
.bs-popover-right .arrow::after {
  left: 1px;
  border-right-color: #000;
  transform: rotate(90deg);
  top: -27px;
  left: 152px;
}
.bs-popover-right .arrow::before {
  display: none;
}
</style>
